import { DragDropProvider } from "@dnd-kit/react";
import { useSortable } from "@dnd-kit/react/sortable";
import { AutoScroller } from "@dnd-kit/dom";
import { move } from "@dnd-kit/helpers";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { getCalendar } from "../../../contexts/CalendarContext";
import { datetime } from "../../../util/library/datetime";
import { aDayMillisecond, FULL_MONTH_YEAR_FORMAT } from "../../../util/varibles/constants";
import { ISite } from "../../../util/varibles/interface";
import { openSitePopup } from "../../LocationPage/Sites/SiteDetail";
import { CALENDAR_ID } from "../constants";
import { overviewActions } from "../reducer";
import styles from './style.module.scss';
import useSiteLayout from "./useSiteLayout";
import useDragScrollUp from "../useDragScrollUp";
import Menu from "../../../components/Menu";
import { loginActions } from "../../LoginPage/reducer";
import { OverviewSetting } from "../../../util/varibles/userSetting";
import { getStore } from "../../../util/store/store";
import { selectOverview } from "../../../util/store/selectors";

const verticalAutoScroll = (plugins: any[]) => plugins.map(plugin =>
    plugin === AutoScroller
        ? AutoScroller.configure({ threshold: { x: 0, y: .2 } })
        : plugin
);

const Month: React.FC = () => {
    const [month, setMonth] = useState(datetime().format(FULL_MONTH_YEAR_FORMAT));
    const get = getCalendar();

    const onScroll = useCallback(() => {
        const el = document.getElementById(CALENDAR_ID);
        if (el) {
            const { widthDay, startTime } = get();
            const { scrollLeft } = el;
            const day = Math.round(Math.max(scrollLeft, 160 + widthDay) / widthDay);
            const time = datetime(startTime + (day * aDayMillisecond));
            setMonth(time.format(FULL_MONTH_YEAR_FORMAT));
        }
    }, [get]);

    useEffect(() => {
        const el = document.getElementById(CALENDAR_ID);
        if (el)
            el.addEventListener('scroll', onScroll);
        return () => {
            if (el)
                el.removeEventListener('scroll', onScroll);
        }
    }, [onScroll]);


    return <div className={styles.month}>
        {month}
    </div>;
};

const Site: React.FC<{ site: ISite, height: number, index: number }> = ({ site, height, index }) => {
    const [element, setElement] = useState<Element | null>(null);
    const handleRef = useRef<HTMLSpanElement | null>(null);
    const { isDragging } = useSortable({
        id: site.id,
        index,
        element,
        handle: handleRef,
    });
    const handleClick = useCallback((data: ISite) => {
        openSitePopup(data);
    }, []);

    return <div
        ref={setElement}
        className={styles.site}
        data-dragging={isDragging || undefined}
        style={{ "--site-height": `${height}px` } as React.CSSProperties}
    >
        <div className={styles.siteName}>
            <span ref={handleRef} className={`${styles.dragHandle} link font-[600]`} onClick={() => handleClick(site)}>{site.name}</span>
        </div>
    </div>
}

const Sites = () => {
    const layout = useSiteLayout();
    const dispatch = useDispatch();
    const [menuOpen, setMenuOpen] = useState(false);
    const getScrollElement = useCallback(() => document.getElementById(CALENDAR_ID), []);
    const { onDragMove, stopDragScrollUp } = useDragScrollUp(getScrollElement);

    const handleSortByName = useCallback(() => {
        const nextSites = layout
            .map(item => item.site)
            .sort((first, second) => first.name.localeCompare(second.name, undefined, {
                sensitivity: 'base',
                numeric: true,
            }));
        dispatch(overviewActions.updateSites(nextSites));
        dispatch(loginActions.updateUserSetting([{
            [OverviewSetting.SITE_ORDER]: [],
        }]));
    }, [dispatch, layout]);

    const handleDragEnd = useCallback(() => {
        stopDragScrollUp();
        const currentSites = selectOverview.sites(getStore());
        dispatch(loginActions.updateUserSetting([{
            [OverviewSetting.SITE_ORDER]: currentSites.map(site => site.id),
        }]));
    }, [dispatch, stopDragScrollUp]);

    const handleDragOver = useCallback((event: any) => {
        const sites = layout.map(item => item.site);
        const nextSites = move(sites, event);
        if (nextSites !== sites)
            dispatch(overviewActions.updateSites(nextSites));
    }, [dispatch, layout]);

    return <Menu
        className={styles.sitesMenu}
        trigger={['contextMenu']}
        visible={menuOpen}
        onOpenChange={setMenuOpen}
        items={[{
            key: 'sort-by-name',
            label: 'Sort by name',
            onClick: handleSortByName,
        }]}
    >
        <div className={styles.sites}>
            <Month />
            <DragDropProvider
                plugins={verticalAutoScroll}
                onDragStart={() => setMenuOpen(false)}
                onDragMove={onDragMove}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
            >
                {layout.map(({ site, height }, index) => <Site
                    key={site.id}
                    index={index}
                    site={site}
                    height={height}
                />)}
            </DragDropProvider>
        </div>
    </Menu>
}

export default Sites;
